class NexusScrollMarquee {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.marquee-track');
    this.items = [...container.querySelectorAll('.marquee-item')];
    this.speedMultiplier = parseFloat(container.dataset.speedMultiplier) || 1;
    this.isPaused = false;
    
    // Clone items for infinite scrolling effect
    this.cloneItems();
    
    // Initialize state
    this.position = 0;
    this.animationFrame = null;
    
    // Set base speed (pixels per frame)
    this.baseSpeed = 1.5;
    
    // Bind event listeners
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleItemMouseEnter = this.handleItemMouseEnter.bind(this);
    this.handleItemMouseLeave = this.handleItemMouseLeave.bind(this);
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Start animation loop
    this.start();
  }
  
  cloneItems() {
    this.items.forEach(item => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true'); // Hide cloned items from screen readers
      this.track.appendChild(clone);
    });
  }
  
  setupEventListeners() {
    this.container.addEventListener('mouseenter', this.handleMouseEnter);
    this.container.addEventListener('mouseleave', this.handleMouseLeave);
    
    // Add hover listeners to individual items for liquid fill
    this.items.forEach(item => {
      const textElement = item.querySelector('.marquee-text');
      if (textElement) {
        item.addEventListener('mouseenter', () => this.handleItemMouseEnter(textElement));
        item.addEventListener('mouseleave', () => this.handleItemMouseLeave(textElement));
      }
    });
  }
  
  handleItemMouseEnter(textElement) {
    // Create liquid fill effect
    this.createLiquidFill(textElement);
  }
  
  handleItemMouseLeave(textElement) {
    // Remove liquid fill effect
    this.removeLiquidFill(textElement);
  }
  
  createLiquidFill(textElement) {
    // Store original text
    const originalText = textElement.textContent;
    
    // Determine if this is odd or even item to set correct fill color
    const parentItem = textElement.closest('.marquee-item');
    const isOdd = [...parentItem.parentNode.children].indexOf(parentItem) % 2 === 0;
    const fillColor = isOdd ? '#000' : '#fff';
    
    // Create overlay element for liquid fill
    const fillOverlay = document.createElement('span');
    fillOverlay.className = 'liquid-fill-overlay';
    fillOverlay.textContent = originalText;
    fillOverlay.style.position = 'absolute';
    fillOverlay.style.top = '0';
    fillOverlay.style.left = '0';
    fillOverlay.style.width = '0%';
    fillOverlay.style.height = '100%';
    fillOverlay.style.color = fillColor;
    fillOverlay.style.webkitTextStroke = '1px #000';
    fillOverlay.style.overflow = 'hidden';
    fillOverlay.style.zIndex = '1';
    fillOverlay.style.fontFamily = 'inherit';
    fillOverlay.style.fontSize = 'inherit';
    fillOverlay.style.fontWeight = 'inherit';
    fillOverlay.style.textTransform = 'inherit';
    fillOverlay.style.lineHeight = 'inherit';
    fillOverlay.style.letterSpacing = 'inherit';
    fillOverlay.style.wordSpacing = 'inherit';
    
    // Add overlay to text element
    textElement.style.position = 'relative';
    textElement.appendChild(fillOverlay);
    
    // Animate the fill from left to right
    let width = 0;
    const animateFill = () => {
      if (width <= 100) {
        fillOverlay.style.width = width + '%';
        width += 4; // Increased speed of fill (was 2)
        requestAnimationFrame(animateFill);
      }
    };
    
    animateFill();
    
    // Store reference for cleanup
    textElement.liquidFillOverlay = fillOverlay;
  }
  
  removeLiquidFill(textElement) {
    // Remove the liquid fill overlay
    if (textElement.liquidFillOverlay) {
      textElement.removeChild(textElement.liquidFillOverlay);
      textElement.liquidFillOverlay = null;
    }
  }
  
  // Auto-scroll logic - moves continuously from right to left
  autoScroll() {
    if (!this.isPaused) {
      // Move left at constant speed multiplied by speed setting
      this.position -= this.baseSpeed * this.speedMultiplier;
      
      // Handle wrapping for infinite loop
      const totalTrackWidth = this.track.scrollWidth || this.track.offsetWidth;
      
      // When items move completely off-screen to the left, reset position
      if (this.position <= -totalTrackWidth) {
        this.position = 0;
      }
    }
  }
  
  handleMouseEnter() {
    this.isPaused = true;
  }
  
  handleMouseLeave() {
    this.isPaused = false;
  }
  
  update() {
    // Apply auto-scroll movement
    this.autoScroll();
    
    // Apply the transform to move the marquee with GPU acceleration
    this.track.style.transform = `translate3d(${this.position}px, 0, 0)`;
    this.track.style.willChange = 'transform'; // Optimize for performance
    
    // Continue the animation loop
    this.animationFrame = requestAnimationFrame(() => this.update());
  }
  
  start() {
    if (!this.animationFrame) {
      this.update();
    }
  }
  
  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    this.container.removeEventListener('mouseenter', this.handleMouseEnter);
    this.container.removeEventListener('mouseleave', this.handleMouseLeave);
    
    // Clean up item event listeners
    this.items.forEach(item => {
      const textElement = item.querySelector('.marquee-text');
      if (textElement && textElement.liquidFillOverlay) {
        this.removeLiquidFill(textElement);
      }
    });
  }
}

// Initialize marquees when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const marqueeContainers = document.querySelectorAll('[data-nexus-marquee]');
  
  marqueeContainers.forEach(container => {
    container.nexusMarqueeInstance = new NexusScrollMarquee(container);
  });
});

// Reinitialize on window resize
window.addEventListener('resize', () => {
  const marqueeContainers = document.querySelectorAll('[data-nexus-marquee]');
  
  marqueeContainers.forEach(container => {
    // Destroy and recreate the instance on resize
    if (container.nexusMarqueeInstance) {
      container.nexusMarqueeInstance.destroy();
    }
    container.nexusMarqueeInstance = new NexusScrollMarquee(container);
  });
});